<template>
  <b-container
    id="content"
    fluid
  >
    <b-tabs>
      <!-- My Networks Tab -->
      <b-tab
        :active="currentTab === 'networks'"
        @click="updateCurrentTab('networks')"
      >
        <template #title>
          <b-spinner
            v-if="!(getNetworksLoaded)"
            type="border"
            small
          />
          <span class="d-inline d-xl-none">{{ $t('views.networks.tabs.my-networks-compact') }}</span>
          <span class="ml-2 d-none d-xl-inline">{{ $t('views.networks.tabs.my-networks') }}</span>
        </template>
        <template v-if="getNetworksLoaded && getNetworks.length === 0">
          <div class="m-4">
            <h3><strong>{{ $t('views.networks.no-networks.title') }}</strong></h3>
            <p>{{ $t('views.networks.no-networks.subtitle') }}</p>
            <b-button
              variant="success"
              @click="showCreateModal"
            >
              <b-icon icon="plus" />
              {{ $t('views.networks.buttons.create') }}
            </b-button>
          </div>
        </template>
        <div v-else>
          <div class="d-flex justify-content-end px-5 pt-3">
            <b-button
              variant="success"
              @click="showCreateModal"
            >
              <b-icon icon="plus" />
              {{ $t('views.networks.buttons.create') }}
            </b-button>
          </div>
          <IsardTable
            :items="getNetworks"
            :loading="!(getNetworksLoaded)"
            :page-options="pageOptions"
            :default-per-page="perPage"
            :filter-on="filterOn"
            :row-class="rowClass"
            :fields="fields"
            class="px-5 pt-3"
          >
            <template #cell(name)="data">
              <p class="m-0 font-weight-bold">
                <b-icon
                  :icon="getNetworkIcon(data.item)"
                  :variant="getNetworkVariant(data.item)"
                  class="mr-2"
                />
                {{ data.item.name }}
              </p>
            </template>
            <template #cell(description)="data">
              <p class="text-dark-gray m-0">
                {{ data.item.description }}
              </p>
            </template>
            <template #cell(model)="data">
              <span class="badge badge-secondary">{{ data.item.model || 'virtio' }}</span>
            </template>
            <template #cell(sharing)="data">
              <span
                v-if="isNetworkShared(data.item)"
                class="text-success"
              >
                <b-icon icon="unlock-fill" />
                {{ $t('views.networks.shared') }}
              </span>
              <span
                v-else
                class="text-muted"
              >
                <b-icon icon="lock-fill" />
                {{ $t('views.networks.private') }}
              </span>
            </template>
            <template #cell(actions)="data">
              <div class="d-flex align-items-center">
                <b-button
                  class="rounded-circle px-2 mr-2 btn-blue"
                  :title="$t('views.networks.buttons.edit')"
                  @click="showEditModal(data.item)"
                >
                  <b-icon
                    icon="pencil"
                    scale="0.75"
                  />
                </b-button>
                <b-button
                  v-if="canShareNetworks"
                  class="rounded-circle px-2 mr-2 btn-dark-blue"
                  :title="$t('views.networks.buttons.sharing')"
                  @click="showAllowedModal(data.item)"
                >
                  <b-icon
                    icon="people-fill"
                    scale="0.75"
                  />
                </b-button>
                <b-button
                  class="rounded-circle px-2 mr-2 btn-gray"
                  :title="$t('views.networks.buttons.usage')"
                  @click="showUsageModal(data.item)"
                >
                  <b-icon
                    icon="bar-chart"
                    scale="0.75"
                  />
                </b-button>
                <b-button
                  class="rounded-circle px-2 mr-2 btn-red"
                  :title="$t('views.networks.buttons.delete')"
                  @click="showDeleteModal(data.item)"
                >
                  <b-icon
                    icon="x"
                    scale="1"
                  />
                </b-button>
              </div>
            </template>
          </IsardTable>
        </div>
      </b-tab>

      <!-- Shared With Me Tab -->
      <b-tab
        :active="currentTab === 'sharedNetworks'"
        @click="updateCurrentTab('sharedNetworks')"
      >
        <template #title>
          <b-spinner
            v-if="!(getSharedNetworksLoaded)"
            type="border"
            small
          />
          <span class="d-inline d-xl-none">{{ $t('views.networks.tabs.shared-networks-compact') }}</span>
          <span class="ml-2 d-none d-xl-inline">{{ $t('views.networks.tabs.shared-networks') }}</span>
        </template>
        <template v-if="getSharedNetworksLoaded && getSharedNetworks.length === 0">
          <div class="m-4">
            <h3><strong>{{ $t('views.networks.no-shared-networks.title') }}</strong></h3>
            <p>{{ $t('views.networks.no-shared-networks.subtitle') }}</p>
          </div>
        </template>
        <IsardTable
          v-else
          :items="getSharedNetworks"
          :loading="!(getSharedNetworksLoaded)"
          :page-options="pageOptions"
          :default-per-page="perPage"
          :filter-on="filterOn"
          :row-class="rowClass"
          :fields="sharedFields"
          class="px-5 pt-3"
        >
          <template #cell(name)="data">
            <p class="m-0 font-weight-bold">
              <b-icon
                icon="people-fill"
                variant="success"
                class="mr-2"
              />
              {{ data.item.name }}
            </p>
          </template>
          <template #cell(description)="data">
            <p class="text-dark-gray m-0">
              {{ data.item.description }}
            </p>
          </template>
          <template #cell(model)="data">
            <span class="badge badge-secondary">{{ data.item.model || 'virtio' }}</span>
          </template>
        </IsardTable>
      </b-tab>
    </b-tabs>

    <!-- Create/Edit Network Modal -->
    <b-modal
      v-model="showNetworkModal"
      :title="editingNetwork ? $t('views.networks.modal.edit-title') : $t('views.networks.modal.create-title')"
      :ok-title="editingNetwork ? $t('views.networks.buttons.save') : $t('views.networks.buttons.create')"
      :cancel-title="$t('forms.cancel')"
      :ok-disabled="!networkForm.name.trim()"
      size="lg"
      @ok="saveNetwork"
      @hidden="resetNetworkForm"
    >
      <b-form-group
        :label="$t('views.networks.modal.name')"
        label-for="network-name"
      >
        <b-form-input
          id="network-name"
          v-model="networkForm.name"
          :placeholder="$t('views.networks.modal.name-placeholder')"
          autofocus
        />
      </b-form-group>
      <b-form-group
        :label="$t('views.networks.modal.description')"
        label-for="network-description"
      >
        <b-form-input
          id="network-description"
          v-model="networkForm.description"
          :placeholder="$t('views.networks.modal.description-placeholder')"
        />
      </b-form-group>
      <b-form-group
        :label="$t('views.networks.modal.model')"
        label-for="network-model"
      >
        <b-form-select
          id="network-model"
          v-model="networkForm.model"
          :options="networkModelOptions"
        />
        <small class="text-muted">{{ $t('views.networks.modal.model-help') }}</small>
      </b-form-group>
      <!-- Sharing section - only for advanced/manager/admin on create -->
      <div v-if="canShareNetworks && !editingNetwork">
        <NetworkAllowedForm
          :show-deployments="true"
          @update:allowed="networkForm.allowed = $event"
        />
      </div>
    </b-modal>

    <!-- Delete Confirmation Modal -->
    <b-modal
      v-model="showDeleteConfirmModal"
      :title="$t('views.networks.modal.delete-title')"
      :ok-title="$t('views.networks.buttons.delete')"
      :cancel-title="$t('forms.cancel')"
      ok-variant="danger"
      @ok="confirmDelete"
    >
      <p>{{ $t('views.networks.modal.delete-confirm', { name: deletingNetwork ? deletingNetwork.name : '' }) }}</p>
      <p
        v-if="deletingNetworkUsage && deletingNetworkUsage.total > 0"
        class="text-warning"
      >
        <b-icon icon="exclamation-triangle" />
        {{ $t('views.networks.modal.delete-warning', { count: deletingNetworkUsage.total }) }}
      </p>
    </b-modal>

    <!-- Usage Modal -->
    <b-modal
      v-model="showUsageInfoModal"
      :title="$t('views.networks.modal.usage-title', { name: usageNetwork ? usageNetwork.name : '' })"
      :ok-title="$t('forms.close')"
      ok-only
      size="lg"
    >
      <div v-if="getNetworkUsage">
        <p>
          <strong>{{ $t('views.networks.modal.usage-total') }}:</strong>
          {{ getNetworkUsage.total }}
        </p>
        <div v-if="getNetworkUsage.domains && getNetworkUsage.domains.length > 0">
          <h6>{{ $t('views.networks.modal.usage-desktops') }}</h6>
          <b-table
            :items="getNetworkUsage.domains"
            :fields="usageFields"
            small
            striped
          />
        </div>
        <p
          v-else
          class="text-muted"
        >
          {{ $t('views.networks.modal.usage-none') }}
        </p>
      </div>
      <div
        v-else
        class="text-center"
      >
        <b-spinner />
      </div>
    </b-modal>

    <AllowedModal
      :show-deployments="true"
      @updateAllowed="updateAllowed"
    />
  </b-container>
</template>

<script>
import IsardTable from '@/components/shared/IsardTable.vue'
import AllowedModal from '@/components/AllowedModal.vue'
import NetworkAllowedForm from '@/components/NetworkAllowedForm.vue'
import { mapGetters, mapActions } from 'vuex'
import { computed, ref, reactive } from '@vue/composition-api'
import i18n from '@/i18n'

export default {
  components: {
    IsardTable,
    AllowedModal,
    NetworkAllowedForm
  },
  setup (_, context) {
    const perPage = ref(10)
    const pageOptions = ref([5, 10, 20, 30, 50, 100])

    const $store = context.root.$store
    $store.dispatch('fetchNetworks')

    const currentTab = ref('networks')

    const updateCurrentTab = (tab) => {
      currentTab.value = tab
    }

    const filterOn = reactive(['name', 'description'])
    const rowClass = 'cursor-pointer'

    // Check if user can share networks (not regular user role)
    const canShareNetworks = computed(() => {
      const user = $store.getters.getUser
      return user && ['advanced', 'manager', 'admin'].includes(user.role_id)
    })

    // Network form state
    const showNetworkModal = ref(false)
    const editingNetwork = ref(null)
    const networkForm = reactive({
      name: '',
      description: '',
      model: 'virtio',
      allowed: { groups: false, users: false, deployments: false }
    })

    const networkModelOptions = computed(() => [
      { value: 'virtio', text: i18n.t('forms.domain.hardware.model-virtio') },
      { value: 'e1000', text: i18n.t('forms.domain.hardware.model-e1000') },
      { value: 'rtl8139', text: i18n.t('forms.domain.hardware.model-rtl8139') }
    ])

    const showCreateModal = () => {
      editingNetwork.value = null
      networkForm.name = ''
      networkForm.description = ''
      networkForm.model = 'virtio'
      networkForm.allowed = { groups: false, users: false, deployments: false }
      showNetworkModal.value = true
    }

    const showEditModal = (network) => {
      editingNetwork.value = network
      networkForm.name = network.name
      networkForm.description = network.description || ''
      networkForm.model = network.model || 'virtio'
      showNetworkModal.value = true
    }

    const resetNetworkForm = () => {
      editingNetwork.value = null
      networkForm.name = ''
      networkForm.description = ''
      networkForm.model = 'virtio'
      networkForm.allowed = { groups: false, users: false, deployments: false }
    }

    const saveNetwork = async (bvModalEvent) => {
      bvModalEvent.preventDefault()
      if (!networkForm.name.trim()) return

      try {
        if (editingNetwork.value) {
          await $store.dispatch('updateNetwork', {
            id: editingNetwork.value.id,
            data: {
              name: networkForm.name.trim(),
              description: networkForm.description.trim(),
              model: networkForm.model
            }
          })
        } else {
          const payload = {
            name: networkForm.name.trim(),
            description: networkForm.description.trim(),
            model: networkForm.model
          }
          // Add allowed if user has permission and any sharing is enabled
          if (canShareNetworks.value) {
            const allowed = networkForm.allowed
            const hasSharing = allowed.groups !== false || allowed.users !== false || allowed.deployments !== false
            if (hasSharing) {
              payload.allowed = {
                roles: false,
                categories: false,
                groups: allowed.groups,
                users: allowed.users,
                deployments: allowed.deployments
              }
            }
          }
          await $store.dispatch('createNetwork', payload)
        }
        showNetworkModal.value = false
        resetNetworkForm()
        $store.dispatch('fetchNetworks')
      } catch (e) {
        // Error handled in store
      }
    }

    // Delete modal state
    const showDeleteConfirmModal = ref(false)
    const deletingNetwork = ref(null)
    const deletingNetworkUsage = ref(null)

    const showDeleteModal = async (network) => {
      deletingNetwork.value = network
      deletingNetworkUsage.value = null
      showDeleteConfirmModal.value = true
      try {
        deletingNetworkUsage.value = await $store.dispatch('fetchNetworkUsage', network.id)
      } catch (e) {
        // Continue anyway
      }
    }

    const confirmDelete = async () => {
      if (!deletingNetwork.value) return
      try {
        await $store.dispatch('deleteNetwork', deletingNetwork.value.id)
        showDeleteConfirmModal.value = false
        deletingNetwork.value = null
      } catch (e) {
        // Error handled in store
      }
    }

    // Usage modal state
    const showUsageInfoModal = ref(false)
    const usageNetwork = ref(null)

    const showUsageModal = async (network) => {
      usageNetwork.value = network
      $store.commit('setNetworkUsage', null)
      showUsageInfoModal.value = true
      try {
        await $store.dispatch('fetchNetworkUsage', network.id)
      } catch (e) {
        // Error handled in store
      }
    }

    // Allowed modal
    const allowedId = computed(() => $store.getters.getId)

    const showAllowedModal = (network) => {
      $store.dispatch('fetchAllowed', { table: 'user_networks', id: network.id })
    }

    const updateAllowed = (allowed) => {
      $store.dispatch('updateAllowed', { table: 'user_networks', id: allowedId.value, allowed: allowed })
      $store.dispatch('fetchNetworks')
    }

    // Helper functions
    const isNetworkShared = (network) => {
      const allowed = network.allowed || {}
      for (const key of ['roles', 'categories', 'groups', 'users', 'deployments']) {
        const val = allowed[key]
        if (val !== false && Array.isArray(val)) {
          return true
        }
      }
      return false
    }

    const getNetworkIcon = (network) => {
      return isNetworkShared(network) ? 'unlock-fill' : 'lock-fill'
    }

    const getNetworkVariant = (network) => {
      return isNetworkShared(network) ? 'warning' : 'info'
    }

    const fields = [
      {
        key: 'name',
        sortable: true,
        label: i18n.t('views.networks.table.name'),
        thStyle: { width: '25%' }
      },
      {
        key: 'description',
        sortable: true,
        label: i18n.t('views.networks.table.description'),
        thStyle: { width: '30%' }
      },
      {
        key: 'model',
        sortable: true,
        label: i18n.t('views.networks.table.model'),
        thStyle: { width: '10%' }
      },
      {
        key: 'sharing',
        sortable: false,
        label: i18n.t('views.networks.table.sharing'),
        thStyle: { width: '15%' }
      },
      {
        key: 'actions',
        label: i18n.t('views.networks.table.actions'),
        thStyle: { width: '20%' }
      }
    ]

    const sharedFields = [
      {
        key: 'name',
        sortable: true,
        label: i18n.t('views.networks.table.name'),
        thStyle: { width: '30%' }
      },
      {
        key: 'description',
        sortable: true,
        label: i18n.t('views.networks.table.description'),
        thStyle: { width: '40%' }
      },
      {
        key: 'model',
        sortable: true,
        label: i18n.t('views.networks.table.model'),
        thStyle: { width: '30%' }
      }
    ]

    const usageFields = [
      { key: 'name', label: i18n.t('views.networks.usage.name') },
      { key: 'user_name', label: i18n.t('views.networks.usage.user') },
      { key: 'status', label: i18n.t('views.networks.usage.status') }
    ]

    return {
      perPage,
      pageOptions,
      currentTab,
      updateCurrentTab,
      filterOn,
      rowClass,
      fields,
      sharedFields,
      usageFields,
      canShareNetworks,
      // Network form
      showNetworkModal,
      editingNetwork,
      networkForm,
      networkModelOptions,
      showCreateModal,
      showEditModal,
      resetNetworkForm,
      saveNetwork,
      // Delete
      showDeleteConfirmModal,
      deletingNetwork,
      deletingNetworkUsage,
      showDeleteModal,
      confirmDelete,
      // Usage
      showUsageInfoModal,
      usageNetwork,
      showUsageModal,
      // Allowed
      showAllowedModal,
      updateAllowed,
      // Helpers
      isNetworkShared,
      getNetworkIcon,
      getNetworkVariant
    }
  },
  computed: {
    ...mapGetters([
      'getNetworks',
      'getNetworksLoaded',
      'getSharedNetworks',
      'getSharedNetworksLoaded',
      'getNetworkUsage'
    ])
  },
  destroyed () {
    this.$store.dispatch('resetNetworksState')
  },
  methods: {
    ...mapActions([])
  }
}
</script>
